import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { QueryCache, hashKey, type Fetcher, type QueryKey, type QueryState } from './cache';

/** The dashboard-wide cache instance. */
export const queryCache = new QueryCache();

export interface ResourceResult<T> extends QueryState<T> {
  /** First load without data. */
  isLoading: boolean;
  refetch: () => Promise<T | undefined>;
  setData: (updater: T | undefined | ((prev: T | undefined) => T | undefined)) => void;
}

const DISABLED_KEY: QueryKey = ['__disabled__'];

/**
 * Keyed resource hook: `useResource(['site', id], (signal) => client.call(...))`.
 * Pass `null` as key to disable fetching (e.g. while a parameter is missing).
 */
export function useResource<T>(key: QueryKey | null, fetcher: Fetcher<T>, cache: QueryCache = queryCache): ResourceResult<T> {
  const effectiveKey = key ?? DISABLED_KEY;
  const hash = hashKey(effectiveKey);
  const keyRef = useRef(effectiveKey);
  keyRef.current = effectiveKey;
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const subscribe = useCallback((cb: () => void) => cache.subscribe(keyRef.current, cb), [cache, hash]);
  const getSnapshot = useCallback(() => cache.getState<T>(keyRef.current), [cache, hash]);
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (!key) return;
    const stable: Fetcher<T> = (signal) => fetcherRef.current(signal);
    cache.register(keyRef.current, stable);
    if (cache.shouldFetch(keyRef.current)) void cache.fetch(keyRef.current, stable).catch(() => undefined);
  }, [cache, hash, key === null]);

  const refetch = useCallback(async () => {
    if (!key) return undefined;
    return cache.fetch<T>(keyRef.current, (signal) => fetcherRef.current(signal), { force: true });
  }, [cache, hash, key === null]);

  const setData = useCallback((updater: T | undefined | ((prev: T | undefined) => T | undefined)) => cache.setData<T>(keyRef.current, updater), [cache, hash]);

  const disabled = key === null;
  return {
    ...(disabled ? { ...state, data: undefined, status: 'idle' as const, isFetching: false } : state),
    isLoading: !disabled && state.data === undefined && (state.status === 'loading' || state.status === 'idle'),
    refetch,
    setData,
  };
}

export interface MutationOptions<TVars, TResult, TCtx> {
  mutationFn: (vars: TVars) => Promise<TResult>;
  /** Runs before the request (optimistic updates). The returned context is passed to onError/onSuccess. */
  onMutate?: (vars: TVars) => TCtx;
  onSuccess?: (result: TResult, vars: TVars, ctx: TCtx | undefined) => void;
  onError?: (error: unknown, vars: TVars, ctx: TCtx | undefined) => void;
  /** Key prefixes to invalidate after success (and after error, to resync). */
  invalidate?: QueryKey[] | ((result: TResult | undefined, vars: TVars) => QueryKey[]);
}

export interface MutationResult<TVars, TResult> {
  /** Fire-and-forget: errors are routed to onError and swallowed. */
  mutate: (vars: TVars) => Promise<TResult | undefined>;
  /** Throws on error. */
  mutateAsync: (vars: TVars) => Promise<TResult>;
  isPending: boolean;
  error: unknown;
  reset: () => void;
}

export function useMutation<TVars, TResult, TCtx = (() => void) | undefined>(
  opts: MutationOptions<TVars, TResult, TCtx>,
  cache: QueryCache = queryCache,
): MutationResult<TVars, TResult> {
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const [pending, setPending] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const mutateAsync = useCallback(
    async (vars: TVars) => {
      const o = optsRef.current;
      const ctx = o.onMutate?.(vars);
      if (mounted.current) {
        setPending((n) => n + 1);
        setError(null);
      }
      const invalidateAll = (result: TResult | undefined) => {
        const keys = typeof o.invalidate === 'function' ? o.invalidate(result, vars) : (o.invalidate ?? []);
        keys.forEach((k) => cache.invalidate(k));
      };
      try {
        const result = await o.mutationFn(vars);
        o.onSuccess?.(result, vars, ctx);
        invalidateAll(result);
        return result;
      } catch (e) {
        if (mounted.current) setError(e);
        o.onError?.(e, vars, ctx);
        throw e;
      } finally {
        if (mounted.current) setPending((n) => n - 1);
      }
    },
    [cache],
  );

  const mutate = useCallback((vars: TVars) => mutateAsync(vars).catch(() => undefined), [mutateAsync]);

  return { mutate, mutateAsync, isPending: pending > 0, error, reset: () => setError(null) };
}
